import { useCallback, useEffect, useState } from 'react';
import {
    loadPricingConfigResponses,
    loadTvPricingResponse
} from '../api/pricingConfigApi';

export const parsePricingConfigResponses = async ({ active, draft, versions, tvPricing }) => {
    if (!active.ok) {
        throw new Error(`Config request failed with status ${active.status}`);
    }
    if (!draft.ok) {
        throw new Error(`Draft config request failed with status ${draft.status}`);
    }
    if (!versions.ok) {
        throw new Error(`Config versions request failed with status ${versions.status}`);
    }

    const activeData = await active.json();
    const draftData = await draft.json();
    const versionsData = await versions.json();
    const tvPricingData = tvPricing?.ok ? await tvPricing.json().catch(() => ({})) : {};

    return {
        config: activeData.config || activeData,
        draftConfig: draftData.config || draftData,
        storageStatus: activeData.storage || activeData.config?.meta?.storageStatus || null,
        draftStatus: draftData.storage?.draft || draftData.config?.meta?.storage || null,
        versions: versionsData.versions || [],
        tvV2Pricing: {
            packages: tvPricingData.packages || [],
            priceTable: tvPricingData.priceTable || {}
        }
    };
};

export const usePricingConfig = () => {
    const [config, setConfig] = useState(null);
    const [draftConfig, setDraftConfig] = useState(null);
    const [storageStatus, setStorageStatus] = useState(null);
    const [draftStatus, setDraftStatus] = useState(null);
    const [versions, setVersions] = useState([]);
    const [tvV2Pricing, setTvV2Pricing] = useState({ packages: [], priceTable: {} });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadConfig = useCallback(async ({ showLoading = true } = {}) => {
        if (showLoading) {
            setLoading(true);
        }
        setError('');

        try {
            const [active, draft, versionsResponse] = await loadPricingConfigResponses();
            const tvPricing = await loadTvPricingResponse();
            const parsed = await parsePricingConfigResponses({
                active,
                draft,
                versions: versionsResponse,
                tvPricing
            });

            setConfig(parsed.config);
            setDraftConfig(parsed.draftConfig);
            setStorageStatus(parsed.storageStatus);
            setDraftStatus(parsed.draftStatus);
            setVersions(parsed.versions);
            setTvV2Pricing(parsed.tvV2Pricing);
        } catch (loadError) {
            setError(loadError.message || 'Failed to load Bundle Builder config.');
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    return {
        config,
        draftConfig,
        storageStatus,
        draftStatus,
        versions,
        tvV2Pricing,
        loading,
        error,
        loadConfig
    };
};
